import * as Cesium from 'cesium';
import { extractLinePositions, getOuterRingCoords } from '../../shared/utils/effectsHelpers';
import { HIGHLIGHT_POINT_OPACITY, HIGHLIGHT_POINT_RING_WIDTH, parseHighlightColor, type HighlightStyle } from '../../shared/utils/toolHighlightSld';
import type { ViewBounds } from './viewport.utils';
import type { AssetDetails } from '../../types/InfrastructureManagerTypes';
import type { MapEngine } from '../../types/CommonMapTypes';

// Selection highlight for asset map assets, shared by Asset Overview and Work Orders.

const HIGHLIGHT_LINE_WIDTH = 4;
const HIGHLIGHT_LINE_CASING = 2;
// Margin around a flown-to asset as a share of its size, and the least in degrees (about 200 m) for a point.
const ASSET_VIEW_PADDING_RATIO = 0.5;
const ASSET_MIN_PADDING = 0.002;

// The repo's default highlight palette, as the query/proximity tools use.
const PALETTE = parseHighlightColor();
const FILL = Cesium.Color.fromCssColorString(PALETTE.fill).withAlpha(PALETTE.fillOpacity);
const STROKE = Cesium.Color.fromCssColorString(PALETTE.polygonStroke);

// The style's colour for both fill and outline, with the palette's fill opacity; the default palette's
// own outline when no colour was chosen. `contrast` is the ring colour when it differs from the colour.
const highlightColors = ({ color: css, ringColor }: HighlightStyle) => {
  const color = css === PALETTE.fill ? undefined : Cesium.Color.fromCssColorString(css);
  const colors = color ? { fill: color.withAlpha(PALETTE.fillOpacity), stroke: color } : { fill: FILL, stroke: STROKE };
  return { ...colors, contrast: ringColor === css ? undefined : Cesium.Color.fromCssColorString(ringColor) };
};

// A single geometry's coordinates as a one-item list, or a multi-geometry's parts.
const partsOf = (geometry: AssetDetails['geometry'], single: string, multi: string): unknown[] => {
  if (geometry.type === single) return [geometry.coordinates];
  return geometry.type === multi && Array.isArray(geometry.coordinates) ? geometry.coordinates : [];
};

const polygonRings = (geometry: AssetDetails['geometry']): Cesium.Cartesian3[][] =>
  partsOf(geometry, 'Polygon', 'MultiPolygon')
    .map((polygon) => getOuterRingCoords({ type: 'Polygon', coordinates: polygon }))
    .filter((ring) => ring.length >= 4)
    .map((ring) => ring.map(([lon, lat]) => Cesium.Cartesian3.fromDegrees(lon, lat)));

const pointPositions = (geometry: AssetDetails['geometry']): Cesium.Cartesian3[] =>
  (partsOf(geometry, 'Point', 'MultiPoint') as number[][])
    .filter((point) => point.length >= 2)
    .map(([lon, lat]) => Cesium.Cartesian3.fromDegrees(lon, lat));

const outlinePrimitive = (lines: Cesium.Cartesian3[][], stroke: Cesium.Color, width = HIGHLIGHT_LINE_WIDTH) =>
  new Cesium.Primitive({
    geometryInstances: lines.map(
      (positions) =>
        new Cesium.GeometryInstance({
          geometry: new Cesium.PolylineGeometry({
            positions,
            width,
            vertexFormat: Cesium.PolylineColorAppearance.VERTEX_FORMAT,
          }),
          attributes: { color: Cesium.ColorGeometryInstanceAttribute.fromColor(stroke) },
        }),
    ),
    appearance: new Cesium.PolylineColorAppearance({ translucent: false }),
    allowPicking: false,
  });

// One small owned collection: a ground fill for polygons, polylines for lines and outlines, points for points.
export const drawAssetHighlight = (collection: Cesium.PrimitiveCollection, geometry: AssetDetails['geometry'], style: HighlightStyle) => {
  const { fill, stroke, contrast } = highlightColors(style);
  const rings = polygonRings(geometry);
  if (rings.length) {
    collection.add(
      new Cesium.GroundPrimitive({
        geometryInstances: rings.map(
          (ring) =>
            new Cesium.GeometryInstance({
              geometry: new Cesium.PolygonGeometry({
                polygonHierarchy: new Cesium.PolygonHierarchy(ring),
                vertexFormat: Cesium.PerInstanceColorAppearance.FLAT_VERTEX_FORMAT,
              }),
              attributes: { color: Cesium.ColorGeometryInstanceAttribute.fromColor(fill) },
            }),
        ),
        appearance: new Cesium.PerInstanceColorAppearance({ flat: true, translucent: true }),
        allowPicking: false,
      }),
    );
    collection.add(outlinePrimitive(rings, contrast ?? stroke));
    return;
  }
  const lines = geometry.type.includes('Line') ? extractLinePositions(geometry) : [];
  if (lines.length) {
    // A contrast colour goes under the line as a casing, drawn first.
    if (contrast) collection.add(outlinePrimitive(lines, contrast, HIGHLIGHT_LINE_WIDTH + 2 * HIGHLIGHT_LINE_CASING));
    collection.add(outlinePrimitive(lines, stroke));
    return;
  }
  // A circle the size of the marker it covers; Cesium draws the ring outside pixelSize. A contrast ring stays solid.
  const points = collection.add(new Cesium.PointPrimitiveCollection()) as Cesium.PointPrimitiveCollection;
  const pointColor = Cesium.Color.fromCssColorString(style.color).withAlpha(HIGHLIGHT_POINT_OPACITY);
  const ringColor = contrast ?? pointColor;
  pointPositions(geometry).forEach((position) =>
    points.add({ position, pixelSize: style.size, color: pointColor, outlineColor: ringColor, outlineWidth: HIGHLIGHT_POINT_RING_WIDTH }),
  );
};

// The box with a margin; a point (zero-size box) gets the minimum margin so it still has a view.
const frameBounds = ([west, south, east, north]: AssetDetails['bbox']): ViewBounds => {
  const padX = Math.max((east - west) * ASSET_VIEW_PADDING_RATIO, ASSET_MIN_PADDING);
  const padY = Math.max((north - south) * ASSET_VIEW_PADDING_RATIO, ASSET_MIN_PADDING);
  return { west: west - padX, south: south - padY, east: east + padX, north: north + padY };
};

const unionBounds = (assets: Pick<AssetDetails, 'bbox'>[]): AssetDetails['bbox'] => [
  Math.min(...assets.map(({ bbox }) => bbox[0])),
  Math.min(...assets.map(({ bbox }) => bbox[1])),
  Math.max(...assets.map(({ bbox }) => bbox[2])),
  Math.max(...assets.map(({ bbox }) => bbox[3])),
];

export const flyToAsset = (engine: MapEngine, { bbox }: Pick<AssetDetails, 'bbox'>) => engine.flyTo(frameBounds(bbox));

// Frames all the assets together, with the same margin as a single asset.
export const flyToAssets = (engine: MapEngine, assets: Pick<AssetDetails, 'bbox'>[]) => {
  if (assets.length) engine.flyTo(frameBounds(unionBounds(assets)));
};
